import GroupIcon from "@mui/icons-material/Group";
import { Avatar, Button, Grid, styled, Typography } from "@mui/material";
import { api, types } from "api";
import { useEffect, useRef, useState } from "react";
import { getInitials } from "helpers/authValidation";
import { parseUTCDate } from "helpers/date";

const PREFIX = "GroupListItem";

const classes = {
	root: `${PREFIX}-root`,
	avatar: `${PREFIX}-avatar`,
	icon: `${PREFIX}-icon`,
	text: `${PREFIX}-text`,
	titleTagContainer: `${PREFIX}-titleTagContainer`,
	owner: `${PREFIX}-owner`,
};

const StyledPaper = styled(Grid)(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
		margin: 0,
		marginBottom: theme.spacing(1),
		padding: theme.spacing(2),
		background: theme.palette.background.paper,
		borderRadius: theme.spacing(1),
		alignItems: "center",
	},
	[`& .${classes.avatar}`]: {
		color: theme.palette.primary.contrastText,
		backgroundColor: theme.palette.secondary.main,
		float: "left",
		padding: theme.spacing(1),
	},
	[`& .${classes.icon}`]: {
		float: "left",
		padding: theme.spacing(1),
	},
	[`& .${classes.text}`]: {
		float: "left",
		padding: theme.spacing(1),
	},
	[`& .${classes.titleTagContainer}`]: {
		flexGrow: "true",
		justifyContent: "space-between",
		alignItems: "center",
	},
	[`& .${classes.owner}`]: {
		float: "left",
		padding: theme.spacing(2),
	},
}));

export interface GroupListItemProps {
	group: types.Group;
	handleClick: (groupId: string) => void;
	labelId: string;
}

export const StudentGroupListItem = ({
	group,
	handleClick,
	labelId,
}: GroupListItemProps): JSX.Element => {
	const [assignmentNum, setAssignmentNum] = useState<number>(0);
	const componentMounted = useRef(true);

	useEffect(() => {
		api
			.getGroupAssignments(group.groupId)
			.then((res) => {
				if (res.status !== 200) {
					return;
				}

				if (componentMounted.current) {
					let i = 0;
					let count = 0;
					const now = new Date();
					if (res.data !== null) {
						while (i < res.data.length) {
							const start = parseUTCDate(res.data[i]!.startDate);
							const end = parseUTCDate(res.data[i]!.endDate);

							if (now >= start && now <= end) {
								count += 1;
							}

							i += 1;
						}
						setAssignmentNum(count);
					}
				}
			})
			.catch(() => {
				setAssignmentNum(0);
			});
	}, [group.groupId]);

	return (
		<StyledPaper
			container
			spacing={2}
			data-testid={labelId}
			className={classes.root}
		>
			<Grid item xs={4}>
				<Grid
					container
					flexDirection="row"
					className={classes.titleTagContainer}
				>
					<Typography align="left">{group.name}</Typography>
					<Grid item className={classes.text}>
						<Typography className={classes.owner}> Owner: </Typography>
						{group.owners.map((value) => {
							return (
								<div key={value.userId} className={classes.text}>
									<Avatar className={classes.avatar}>
										{value && getInitials(value.firstName, value.lastName)}
									</Avatar>
									<Typography className={classes.text}>
										{value.firstName} {value.lastName}
									</Typography>
								</div>
							);
						})}
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={3} textAlign="center">
				<Typography> Active Assignments: {assignmentNum}</Typography>
			</Grid>
			<Grid item xs={3} textAlign="center">
				<GroupIcon fontSize="large" className={classes.icon} />
				<Typography className={classes.text}>
					{group.students.length} students
				</Typography>
			</Grid>
			<Grid item xs={2} textAlign="end">
				<Button
					variant="contained"
					onClick={() => handleClick(group.groupId)}
					data-testid="view-group-btn"
				>
					View
				</Button>
			</Grid>
		</StyledPaper>
	);
};
